'use client'

import { useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'
import { useRouter, usePathname } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { toast } from '@/lib/toast'
import { createGiftCardApi } from '@/lib/commerce/services/giftcard-api'
import {
  formatDateTimeForServer,
  validateGiftCardForm,
  validateGiftCardStep,
} from '@/utils/giftCard'

type UseGiftCardFormArgs = {
  templates: any[]
  vendorData: any
  totalSteps: number
}

export function useGiftCardForm({ templates, vendorData, totalSteps }: UseGiftCardFormArgs) {
  const { data: session } = useSession()
  const router = useRouter()
  const pathname = usePathname()

  const isGuest = !session?.user?.id

  const [step, setStep] = useState(0)
  const [giftCardTemplateId, setGiftCardTemplateId] = useState('')
  const [amount, setAmount] = useState('')
  const [senderEmail, setSenderEmail] = useState(session?.user?.email || '')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(new Date(Date.now()))
  const [sendCopy, setSendCopy] = useState(false)
  const [loading, setLoading] = useState(false)

  const vendorId: string | undefined = session?.user?.vendorId as string | undefined
  const sessionId: string | null = session
    ? (isGuest ? (session.sessionId || null) : (session.user?.id || session.sessionId || null))
    : null

  const selectedTemplate = useMemo(() => templates?.find((t: any) => t.id === giftCardTemplateId), [templates, giftCardTemplateId])

  const reset = () => {
    setGiftCardTemplateId('')
    setAmount('')
    setSenderEmail('')
    setFullName('')
    setEmail('')
    setMessage('')
    setDeliveryDate(new Date(Date.now()))
    setSendCopy(false)
  }

  const formState = { giftCardTemplateId, amount, senderEmail, fullName, email }

  const next = () => {
    const err = validateGiftCardStep(step, formState)
    if (err) return toast.error(err)
    setStep(s => Math.min(totalSteps - 1, s + 1))
  }

  const back = () => setStep(s => Math.max(0, s - 1))

  const submit = async () => {
    const err = validateGiftCardForm(formState)
    if (err) return toast.error(err)
    if (!vendorId) return toast.error('Дэлгүүрийн мэдээлэл олдсонгүй')
    setLoading(true)
    const { data, error } = await createGiftCardApi({
      vendorId,
      sessionId,
      isGuest,
      accessToken: session?.user?.accessToken,
      body: {
        giftCardTemplateId,
        amount: parseFloat(amount),
        senderEmail,
        fullName,
        email,
        message,
        deliveryDate: formatDateTimeForServer(deliveryDate),
        sendCopy,
      },
    })
    setLoading(false)
    if (error || !data) return toast.error(error || 'Бэлгийн картыг захиалахад алдаа гарлаа')
    toast.success('Бэлгийн карт захиалга амжилттай хийгдлээ')
    reset()
    router.push(navigateUrl(`/gift/pay/${data.customId}`, pathname))
  }

  return {
    step, setStep, next, back,
    giftCardTemplateId, setGiftCardTemplateId, selectedTemplate,
    amount, setAmount,
    senderEmail, setSenderEmail,
    fullName, setFullName,
    email, setEmail,
    message, setMessage,
    deliveryDate, setDeliveryDate,
    sendCopy, setSendCopy,
    loading,
    isGuest, session, vendorData,
    submit, reset,
  }
}
